import _extends from "@babel/runtime/helpers/extends";
import React, { Component, createRef, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { mdiCheck, mdiEyeOffOutline, mdiEyeOutline, mdiLoading } from '@mdi/js';
import warning from '../utils/console-deprecated';
import Icon from '../Icon';
import FormMessage from '../FormMessage';
import { autofocusLegacyComponentDidMount } from '../hooks/useAutofocus';
import { combineRefs } from '../utils/ref';
import focusInCurrentTarget from '../utils/focus-in-current-target';
import { getLabelId } from '../utils/aria';
import IconButton from '../IconButton';
import IconList from '../IconList';
import IconItem from '../IconItem';
import { LoadingAnnouncerAsComponent } from '../LoadingAnnouncer/useAnnouncer';
import { Container, Label, Input, TextArea, InputLabelContainer, PrefixContainer, SuffixContainer, ContextHelp, StyledCheckListContainer, StyledCheckListText } from './styles';
import * as Sizes from './sizes';
import MaskedInput from './masked';
import Text from '../Typography/Text';
const IconOrIconButton = props => {
  const IconComponent = props.onClick ? IconButton : Icon;
  return /*#__PURE__*/React.createElement(IconComponent, props);
};
export default class TextInput extends Component {
  static propTypes = {
    autofocus: PropTypes.bool,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    mask: PropTypes.oneOfType([
    // eslint-disable-next-line react/forbid-prop-types
    PropTypes.array, PropTypes.func, PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      pattern: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
      // eslint-disable-next-line react/forbid-prop-types
      hints: PropTypes.array,
      pipe: PropTypes.func,
      help: PropTypes.func
    })]),
    label: PropTypes.node,
    ariaLabel: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
    hints: PropTypes.arrayOf(PropTypes.node),
    error: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.bool]),
    name: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    clickableLabel: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    size: PropTypes.string,
    suffixIconProps: PropTypes.shape(Icon.propTypes),
    suffixBackgroundColor: PropTypes.string,
    floating: PropTypes.bool,
    floatingActive: PropTypes.bool,
    multiline: PropTypes.bool,
    height: PropTypes.string,
    contextHelp: PropTypes.bool,
    transparent: PropTypes.bool,
    conceal: PropTypes.bool,
    assistiveHelp: PropTypes.bool,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    checklist: PropTypes.arrayOf(PropTypes.shape({
      message: PropTypes.string,
      validate: PropTypes.func
    })),
    reverseFocus: PropTypes.bool,
    loading: PropTypes.bool,
    required: PropTypes.bool,
    showRequired: PropTypes.bool
  };
  static defaultProps = {
    autofocus: undefined,
    mask: null,
    label: null,
    hints: null,
    error: null,
    name: null,
    onFocus: null,
    onBlur: null,
    onChange: null,
    placeholder: null,
    type: 'text',
    value: undefined,
    clickableLabel: false,
    size: Sizes.normal,
    suffixIconProps: null,
    suffixBackgroundColor: null,
    floating: true,
    floatingActive: false,
    multiline: false,
    height: undefined,
    contextHelp: false,
    transparent: false,
    conceal: false,
    innerRef: undefined,
    ariaLabel: undefined,
    ariaLabelledBy: undefined,
    assistiveHelp: true,
    checklist: [],
    reverseFocus: false,
    loading: false,
    required: false,
    showRequired: false
  };
  state = {
    focused: false,
    value: '',
    visible: !this.props.conceal
  };
  loadingIconProps = {
    icon: mdiLoading,
    role: 'img',
    label: 'Loading animation',
    color: 'actionablePrimary',
    spin: true
  };
  constructor(props) {
    super(props);
    const {
      value
    } = props;
    this.inputRef = /*#__PURE__*/createRef();
    this.isControlled = value !== undefined;

    // DOM ids
    this.labelId = props.ariaLabelledBy || getLabelId(props.name);
    this.contextHelpId = `${this.labelId}-tooltip`;
    this.errorId = `${this.labelId}-error`;
  }
  componentDidMount() {
    autofocusLegacyComponentDidMount({
      ref: this.inputRef,
      autofocus: this.props.autofocus
    });
  }
  getInput() {
    const {
      multiline,
      mask
    } = this.props;
    if (multiline) return TextArea;
    return mask ? MaskedInput : Input;
  }
  getValue() {
    const {
      value: propValue
    } = this.props;
    const {
      value: stateValue
    } = this.state;
    return this.isControlled ? propValue : stateValue;
  }
  shouldRenderError() {
    const {
      error
    } = this.props;

    // Error could be a string, a boolean or a React element, we should only
    // render when it's a string or a React element
    return /*#__PURE__*/isValidElement(error) || error?.length > 0;
  }
  hasError() {
    const {
      error
    } = this.props;
    return error === true || this.shouldRenderError();
  }
  ariaLabelledBy() {
    const {
      ariaLabel
    } = this.props;
    return !ariaLabel ? this.labelId : undefined;
  }
  ariaDescribedBy() {
    const {
      hints,
      error
    } = this.props;

    // if error === true you have to provide an aria-describedby describing the error,
    // in general you have a <FormMessage error /> below the input showing the error
    // description
    if (error === true && !this.props['aria-describedby']) {
      warning(`TextInput / using error === true requires to include aria-describedby in props for screen readers`);
    }
    const describedby = [this.props['aria-describedby'],
    // provided by the user
    hints?.length > 0 ? this.contextHelpId : false,
    // context help
    this.shouldRenderError() ? this.errorId : false // error messages
    ].filter(Boolean).join(' ');
    return describedby.length === 0 ? undefined : describedby;
  }
  handleFocus = e => {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (onFocus) {
      onFocus(e);
    }
  };
  handleBlur = e => {
    const {
      contextHelp,
      onBlur
    } = this.props;
    if (!contextHelp || !focusInCurrentTarget(e)) {
      this.setState({
        focused: false
      });
      if (onBlur) {
        onBlur(e);
      }
    }
  };
  handleChange = e => {
    const {
      value
    } = e.target;
    const {
      onChange
    } = this.props;
    if (!this.isControlled) {
      this.setState({
        value
      });
    }
    if (onChange) {
      onChange(e, value);
    }
  };
  contextHelpProps = () => {
    const {
      contextHelp
    } = this.props;
    return {
      id: this.contextHelpId,
      ...(contextHelp ? {
        role: 'tooltip'
      } : {})
    };
  };
  renderHints() {
    const {
      contextHelp,
      hints = [],
      mask,
      assistiveHelp,
      checklist = []
    } = this.props;
    const {
      focused
    } = this.state;
    const value = this.getValue();
    const hintEntries = hints?.length ? hints.map(hint => /*#__PURE__*/React.createElement(FormMessage, {
      key: hint,
      contextHelp
    }, hint)) : null;
    const checkListEntries = checklist?.length ? /*#__PURE__*/React.createElement(StyledCheckListContainer, {
      $isContextHelpActive: contextHelp
    }, /*#__PURE__*/React.createElement(IconList, null, checklist.map(({
      message,
      validate
    }) => /*#__PURE__*/React.createElement(IconItem, {
      key: message,
      icon: validate(value) ? mdiCheck : ''
    }, /*#__PURE__*/React.createElement(StyledCheckListText, {
      $isValid: validate(value)
    }, message))))) : null;
    if ((hintEntries || checkListEntries) && (focused && contextHelp || !contextHelp)) {
      return /*#__PURE__*/React.createElement("div", this.contextHelpProps(), /*#__PURE__*/React.createElement("div", {
        className: "sr-only"
      }, "Context Help:"), hintEntries, checkListEntries, assistiveHelp && mask?.help && /*#__PURE__*/React.createElement(FormMessage, {
        className: "sr-only"
      }, mask.help(value)));
    }
    return null;
  }
  renderInput() {
    const {
      autofocus: _autofocus,
      clickableLabel,
      label,
      ariaLabel,
      error,
      hints,
      size,
      suffixIconProps,
      suffixBackgroundColor,
      prefixIconProps,
      prefixBackgroundColor,
      prefixCustomImgProps,
      floating,
      floatingActive,
      height,
      multiline,
      contextHelp,
      transparent,
      onBlur,
      innerRef,
      conceal,
      assistiveHelp,
      checklist,
      ariaLabelledBy,
      loading,
      'aria-required': ariaRequired,
      required,
      showRequired,
      ...inputProps
    } = this.props;
    const {
      focused,
      visible
    } = this.state;
    const value = this.getValue();
    const InputComponent = this.getInput();
    return /*#__PURE__*/React.createElement(InputComponent, _extends({
      "data-error": error,
      "data-floating": floating ? 'Floating' : '',
      "data-focused": focused,
      "data-size": Sizes.getRef(size),
      "data-height": height,
      "data-multiline": multiline,
      "data-show-placeholder": focused || !floating,
      "data-transparent": transparent,
      ref: combineRefs([innerRef, this.inputRef])
    }, inputProps, {
      // eslint-disable-next-line no-nested-ternary
      type: conceal ? visible ? 'text' : 'password' : inputProps.type,
      value,
      'aria-label': ariaLabel,
      'aria-invalid': this.hasError(),
      'aria-labelledby': this.ariaLabelledBy(),
      'aria-describedby': this.ariaDescribedBy(),
      'aria-busy': loading,
      'aria-required': required || ariaRequired,
      onFocus: this.handleFocus,
      onChange: this.handleChange
    }));
  }
  render() {
    const {
      clickableLabel,
      error,
      label,
      size,
      multiline,
      suffixIconProps,
      suffixBackgroundColor,
      prefixIconProps,
      prefixBackgroundColor,
      prefixCustomImgProps,
      floating,
      floatingActive,
      transparent,
      disabled,
      conceal = false,
      reverseFocus,
      loading,
      showRequired
    } = this.props;
    const {
      focused,
      visible
    } = this.state;
    const value = this.getValue();
    const computedSize = Sizes.getRef(size);
    const commonPropsForPrefixAndSuffix = {
      $disabled: disabled,
      $error: error,
      $focused: focused,
      $size: computedSize
    };
    return /*#__PURE__*/React.createElement(ContextHelp, {
      onBlur: this.handleBlur
    }, /*#__PURE__*/React.createElement(LoadingAnnouncerAsComponent, {
      active: loading
    }), /*#__PURE__*/React.createElement(Container, {
      className: "input-text-container",
      "data-error": error,
      "data-focused": focused,
      "data-size": computedSize,
      "data-multiline": multiline,
      "data-transparent": transparent,
      $reverseFocus: reverseFocus
    }, prefixIconProps && /*#__PURE__*/React.createElement(PrefixContainer, _extends({}, commonPropsForPrefixAndSuffix, {
      $color: prefixBackgroundColor
    }), /*#__PURE__*/React.createElement(IconOrIconButton, _extends({
      size: "small"
    }, prefixIconProps))), prefixCustomImgProps && /*#__PURE__*/React.createElement(PrefixContainer, _extends({}, commonPropsForPrefixAndSuffix, {
      $color: prefixBackgroundColor
    }), /*#__PURE__*/React.createElement("img", _extends({}, prefixCustomImgProps, {
      alt: prefixCustomImgProps?.alt
    }))), /*#__PURE__*/React.createElement(InputLabelContainer, {
      disabled: disabled && !transparent
    }, this.renderInput(), floating && /*#__PURE__*/React.createElement(Label, {
      "data-clickable": clickableLabel,
      "data-focused": focused,
      "data-error": error,
      "data-multiline": multiline,
      "data-size": computedSize,
      "data-floating": focused || value || value === 0 || floatingActive ? 'Floating' : '',
      id: this.labelId,
      disabled: disabled
    }, label, showRequired && /*#__PURE__*/React.createElement(Text, {
      color: "semanticError"
    }, "*"))), !conceal && suffixIconProps && !loading && /*#__PURE__*/React.createElement(SuffixContainer, _extends({}, commonPropsForPrefixAndSuffix, {
      $color: suffixBackgroundColor
    }), /*#__PURE__*/React.createElement(IconOrIconButton, _extends({
      size: "small"
    }, suffixIconProps))), !conceal && loading && /*#__PURE__*/React.createElement(SuffixContainer, _extends({}, commonPropsForPrefixAndSuffix, {
      $color: suffixBackgroundColor
    }), /*#__PURE__*/React.createElement(IconOrIconButton, _extends({
      size: "small"
    }, this.loadingIconProps))), conceal && /*#__PURE__*/React.createElement(SuffixContainer, commonPropsForPrefixAndSuffix, /*#__PURE__*/React.createElement(IconButton, {
      "aria-pressed": visible,
      size: "1.5rem",
      color: "content",
      icon: visible ? mdiEyeOffOutline : mdiEyeOutline,
      label: `Show ${label}.`,
      title: `Show ${label}.`,
      onClick: () => this.setState({
        visible: !visible
      }),
      "data-auto": "visibilityToggle"
    }))), this.renderHints(), this.shouldRenderError() && /*#__PURE__*/React.createElement(FormMessage, {
      error: true,
      id: this.errorId
    }, error));
  }
}
//# sourceMappingURL=index.js.map